import { Button } from '../ui/Button';
import { Icon } from '../ui/Icon';
import { useSession } from '../../state/session';
import type { SignedOutReason } from '../../web/studioSession';
import styles from './SignInScreen.module.css';

/**
 * The studio's front door.
 *
 * The only page this app serves to someone it has not identified, so it says as
 * little as possible: a mark, a sign-in button, and — when there is one — the
 * reason the last attempt did not work. No profile names, no server name, no
 * hint of what is behind it.
 *
 * `denied` is deliberately not an error state. Being a client here is not a
 * failure, it is simply the wrong door: the game lives in Discord, and saying
 * so is more useful than an access-denied banner.
 */

interface Message {
  tone: 'neutral' | 'warning';
  title: string;
  body: string;
}

function messageFor(reason: SignedOutReason, denied: boolean): Message | null {
  if (denied || reason === 'staff') {
    return {
      tone: 'neutral',
      title: 'This account plays in Discord',
      body: 'The studio is for the server owner and the models who run a realm. Everything a player does happens inside Discord — there is nothing to sign in to here.',
    };
  }

  switch (reason) {
    case 'declined':
      return {
        tone: 'warning',
        title: 'Discord sign-in was cancelled',
        body: 'You closed the Discord screen before approving. Nothing was shared.',
      };
    case 'stale':
      return {
        tone: 'warning',
        title: 'That sign-in expired',
        body: 'It took too long, or the link was opened out of order. Starting again fixes it.',
      };
    case 'exchange':
      return {
        tone: 'warning',
        title: 'Discord answered, the studio did not',
        body: 'Your account is fine — the studio could not finish the handshake. Try once more; if it keeps happening the server needs a look.',
      };
    default:
      return null;
  }
}

export function SignInScreen() {
  const { status, signedOutReason, signIn } = useSession();
  const denied = status === 'denied';
  const message = messageFor(signedOutReason, denied);

  return (
    <div className={styles.screen}>
      <div className={styles.glow} aria-hidden="true" />

      <div className={styles.card}>
        <span className={styles.mark}>
          <Icon name="logo" size={34} />
        </span>

        <div className={styles.heading}>
          <h1 className={styles.title}>Spoyl Studio</h1>
          <p className={styles.tagline}>Spin for your clients, on camera.</p>
        </div>

        {message && (
          <div className={`${styles.message} ${message.tone === 'warning' ? styles.warning : ''}`}>
            <strong className={styles.messageTitle}>{message.title}</strong>
            <span className={styles.messageBody}>{message.body}</span>
          </div>
        )}

        {!denied && (
          <Button variant="primary" size="lg" icon="shield" fullWidth onClick={signIn}>
            Continue with Discord
          </Button>
        )}

        <p className={styles.note}>
          {denied
            ? 'Signed in with the wrong account? Sign in again with the one that runs your profile.'
            : 'Use the Discord account that runs your profile. The studio never sees your password.'}
        </p>

        {denied && (
          <Button variant="ghost" icon="refresh" onClick={signIn}>
            Use a different account
          </Button>
        )}
      </div>
    </div>
  );
}
